import React, { HTMLProps, Suspense, forwardRef, lazy, useMemo, useRef } from "react";
import { CSSProperties, useTheme } from "styled-components";
import mergeRefs from "react-merge-refs";
import { checkTextClamped } from "core/utils/textHelper";
import { DesignStyle } from "..";
import { TextStyled, TextType } from "./_textStyle";

const MouseTooltip = lazy(() => import("ui/MouseTooltip"));

type Props = TextType &
  Omit<HTMLProps<HTMLParagraphElement>, "size"> & {
    href?: string;
    children?: React.ReactNode;
    style?: CSSProperties;
    fontFamily?: string;
    tooltip?: {
      id: string;
      text?: string | null;
      showTooltipOnClamp?: boolean;
      delayShow?: number;
    };
    dangerouslySetInnerHTML?: {
      __html: string;
    };
    "data-testid"?: string;
  };

export const Text = forwardRef<HTMLParagraphElement, Props>((props, ref) => {
  const { children, tooltip, ...rest } = props;

  const textRef = useRef<HTMLParagraphElement>(null);

  const isTextClamped = checkTextClamped(textRef);

  const tooltipText = useMemo(() => {
    if (tooltip?.showTooltipOnClamp) {
      return isTextClamped ? tooltip?.text : "";
    }

    return tooltip?.text;
  }, [tooltip, isTextClamped]);

  return (
    <>
      <TextStyled
        data-for={tooltip?.id}
        data-tip={tooltipText}
        ref={mergeRefs([ref, textRef])}
        {...rest}>
        {children}
      </TextStyled>
      {tooltip && (
        <Suspense fallback={null}>
          <MouseTooltip
            id={tooltip?.id}
            effect="solid"
            place="bottom"
            delayShow={tooltip?.delayShow}
          />
        </Suspense>
      )}
    </>
  );
});

export const TextBold = forwardRef<HTMLParagraphElement, Props>((props, ref) => {
  const theme = useTheme();

  return (
    <Text
      fontFamily={
        theme.designStyle === DesignStyle.PROFESSIONAL ? "Rubik-Bold" : "Nunito-Sans-Bold"
      }
      ref={ref}
      {...props}
    />
  );
});

export const TextSemibold = forwardRef<HTMLParagraphElement, Props>((props, ref) => {
  const theme = useTheme();

  return (
    <Text
      fontFamily={
        theme.designStyle === DesignStyle.PROFESSIONAL ? "Rubik-Semi-Bold" : "Nunito-Sans-SemiBold"
      }
      ref={ref}
      {...props}
    />
  );
});
